import { Component, Input, Output, EventEmitter, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { RoiCalculatorService, RoiResults, WorkflowDistribution } from '../../services/roi-calculator.service';

@Component({
    selector: 'app-roi-calculation',
    imports: [CommonModule, FormsModule, TranslateModule],
    templateUrl: './roi-calculation.component.html',
    styleUrl: './roi-calculation.component.scss'
})
export class RoiCalculationComponent implements OnInit {
  private readonly roiCalculatorService = inject(RoiCalculatorService);

  @Input() teamSize = 0;
  @Input() averageHourlyRate = 0;
  @Input() projectsPerYear = 0;
  @Input() averageLicenceCost = 0;
  @Input() currencyCode = 'EUR';
  @Input() maturityLevel = 20;
  @Input() productivityGainPercent = 0;
  @Input() reworkReductionPercent = 0;
  @Input() coordinationSavingsPercent = 0;

  private static readonly CURRENCY_SYMBOLS: Record<string, string> = {
    EUR: '€', USD: '$', GBP: '£', CHF: 'CHF', SEK: 'kr',
    NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', AED: 'AED', INR: '₹'
  };

  get currencySymbol(): string {
    return RoiCalculationComponent.CURRENCY_SYMBOLS[this.currencyCode] ?? this.currencyCode;
  }

  @Output() teamSizeChange = new EventEmitter<number>();
  @Output() averageHourlyRateChange = new EventEmitter<number>();
  @Output() projectsPerYearChange = new EventEmitter<number>();
  @Output() averageLicenceCostChange = new EventEmitter<number>();
  @Output() maturityLevelChange = new EventEmitter<number>();
  @Output() productivityGainPercentChange = new EventEmitter<number>();
  @Output() reworkReductionPercentChange = new EventEmitter<number>();
  @Output() coordinationSavingsPercentChange = new EventEmitter<number>();

  showMoreInputs = true;

  currentDistribution: WorkflowDistribution = this.roiCalculatorService.getWorkflowDistribution(this.maturityLevel);
  roiResults!: RoiResults;

  ngOnInit(): void {
    this.currentDistribution = this.roiCalculatorService.getWorkflowDistribution(this.maturityLevel);
    this.roiResults = this.computeRoi();
  }

  updateField(field: string, value: number): void {
    const safeValue = Number.isFinite(value) ? Math.max(0, value) : 0;
    (this as any)[field] = safeValue;
    const emitter = (this as any)[field + 'Change'] as EventEmitter<number>;
    if (emitter) {
      emitter.emit(safeValue);
    }

    this.recalculate();
  }

  increment(field: string): void {
    const currentValue = (this as any)[field] as number;
    this.updateField(field, currentValue + 1);
  }

  decrement(field: string): void {
    const currentValue = (this as any)[field] as number;
    this.updateField(field, Math.max(0, currentValue - 1));
  }

  onMaturityChange(): void {
    this.currentDistribution = this.roiCalculatorService.getWorkflowDistribution(this.maturityLevel);
    // Reset percentages to interpolated defaults and emit all changes
    const defaults = this.roiCalculatorService.getImprovementPotential(this.currentDistribution);
    this.productivityGainPercent = defaults.modelingImprovement;
    this.reworkReductionPercent = defaults.reworkImprovement;
    this.coordinationSavingsPercent = defaults.coordinationImprovement;
    this.maturityLevelChange.emit(this.maturityLevel);
    this.productivityGainPercentChange.emit(this.productivityGainPercent);
    this.reworkReductionPercentChange.emit(this.reworkReductionPercent);
    this.coordinationSavingsPercentChange.emit(this.coordinationSavingsPercent);
    this.recalculate();
  }

  onImprovementChange(field: 'productivityGainPercent' | 'reworkReductionPercent' | 'coordinationSavingsPercent', value: number): void {
    const safeValue = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
    (this as any)[field] = safeValue;
    const emitter = (this as any)[field + 'Change'] as EventEmitter<number>;
    emitter.emit(safeValue);
    this.recalculate();
  }

  toggleMoreInputs(): void {
    this.showMoreInputs = !this.showMoreInputs;
  }

  get maturitySliderBackground(): string {
    return `linear-gradient(90deg, #0066cc 0%, #0066cc ${this.maturityLevel}%, #b4daff ${this.maturityLevel}%, #b4daff 100%)`;
  }

  get displayedProductivityGain(): number { return Math.round(this.productivityGainPercent); }
  get displayedReworkReduction(): number { return Math.round(this.reworkReductionPercent); }
  get displayedCoordinationSavings(): number { return Math.round(this.coordinationSavingsPercent); }

  private recalculate(): void {
    this.roiResults = this.computeRoi();
  }

  private computeRoi(): RoiResults {
    return this.roiCalculatorService.calculateRoi({
      teamSize: this.teamSize,
      averageHourlyRate: this.averageHourlyRate,
      projectsPerYear: this.projectsPerYear,
      averageLicenceCost: this.averageLicenceCost,
      maturityLevel: this.maturityLevel,
      productivityGainPercent: this.productivityGainPercent,
      reworkReductionPercent: this.reworkReductionPercent,
      coordinationSavingsPercent: this.coordinationSavingsPercent
    });
  }

  formatCurrency(value: number): string {
    return this.roiCalculatorService.formatCurrency(value);
  }

  formatDecimal(value: number): string {
    return this.roiCalculatorService.formatDecimal(value);
  }
}
